import React from "react";
import {Modal, Paper, Avatar, Chip, Button} from "@material-ui/core";
import {CallMade} from "@material-ui/icons";
import {makeStyles} from "@material-ui/styles";
import _ from "lodash";
import {importedChanges, isImportedRow, isImportedScalar} from "../../../helpers/matchReview";

const DATASET_LABELS = {
    RIA: 'RIA',
    FO: 'Family Office',
    BT: 'Bank & Trust',
    EF: 'E&F',
};

const IMPORTED = '#1b873f';
const BORDER = '#e7e7ea';
const LABEL_COLUMN = 130;
const RECORD_COLUMN = 280;

const useStyles = makeStyles(() => ({
    modal: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        outline: 'none',
        width: 'fit-content',
        minWidth: 560,
        maxWidth: '92vw',
        maxHeight: '88vh',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24,
        padding: '10px 16px',
        borderBottom: `1px solid ${BORDER}`,
    },
    title: {
        margin: 0,
        fontSize: 17,
        fontWeight: 700,
    },
    headerMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        color: '#6b6b76',
        fontSize: 12,
    },
    legend: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        color: IMPORTED,
    },
    dot: {
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: IMPORTED,
        display: 'inline-block',
        flexShrink: 0,
    },
    scroller: {
        overflow: 'auto',
        padding: '0 16px 12px',
    },
    // One grid for every record, so a row takes the height of its tallest cell and CRD, LinkedIn,
    // etc. line up across the columns.
    grid: {
        display: 'grid',
    },
    cell: {
        padding: '10px 12px',
        borderBottom: `1px solid ${BORDER}`,
        minWidth: 0,
        overflowWrap: 'anywhere',
    },
    formCell: {
        backgroundColor: '#f5f8ff',
    },
    headCell: {
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: '#fff',
        borderBottom: `2px solid ${BORDER}`,
    },
    formHeadCell: {
        backgroundColor: '#f5f8ff',
    },
    labelCell: {
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontSize: 11,
        fontWeight: 700,
        color: '#5f5f6b',
        paddingLeft: 0,
    },
    identity: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    },
    avatar: {
        width: 44,
        height: 44,
        fontSize: 18,
        flexShrink: 0,
    },
    importedAvatar: {
        boxShadow: `0 0 0 2px ${IMPORTED}`,
    },
    name: {
        margin: 0,
        fontSize: 15,
        fontWeight: 700,
        lineHeight: 1.25,
    },
    chip: {
        marginTop: 4,
        height: 18,
        fontSize: 11,
    },
    value: {
        fontSize: 14,
        fontWeight: 700,
        color: '#1d1d24',
        lineHeight: 1.3,
    },
    secondary: {
        fontSize: 11,
        color: '#6b6b76',
        lineHeight: 1.4,
    },
    item: {
        marginBottom: 6,
        '&:last-child': {
            marginBottom: 0,
        },
    },
    imported: {
        color: IMPORTED,
        '& $value, & $secondary': {
            color: IMPORTED,
        },
    },
    importedMark: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
    },
    link: {
        color: '#224edf',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        fontSize: 12,
        wordBreak: 'break-all',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
        padding: '10px 16px',
        borderTop: `1px solid ${BORDER}`,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
    },
    designation: {
        height: 20,
        fontSize: 11,
        fontWeight: 700,
        color: '#4a4a55',
        backgroundColor: '#ececf0',
    },
    importedDesignation: {
        color: IMPORTED,
        backgroundColor: '#e3f4e8',
    },
    empty: {
        fontSize: 12,
        color: '#a0a0aa',
    },
}));

const years = (row) => {
    if (!row.start_year && !row.end_year) return null;

    return `${row.start_year || '?'} – ${row.end_year || 'Present'}`;
};

const linkedinLabel = (url) => url.replace(/^https?:\/\//, '').replace(/^www\./, '');

const Imported = ({imported, classes, children}) => (
    <span className={imported ? `${classes.imported} ${classes.importedMark}` : undefined}>
        {imported && <span className={classes.dot}/>}
        {children}
    </span>
);

const Empty = ({classes}) => <div className={classes.empty}>-</div>;

const ItemList = ({items, render, classes}) => (
    items.length === 0 ? <Empty classes={classes}/> : items.map(render)
);

// Each row of the comparison: its label and how one record's cell renders. `scalar` and `row`
// say whether a value is one the import brings into the form.
const ROWS = [
    {
        label: 'Current firm(s)',
        render: (card, {classes}) => (
            <ItemList items={card.current_firms || []} classes={classes} render={(firm, index) => (
                <div key={`${firm.name}-${index}`} className={classes.item}>
                    <div className={classes.value}>{firm.name}</div>
                    {firm.title && <div className={classes.secondary}>{firm.title}</div>}
                </div>
            )}/>
        ),
    },
    {
        label: 'CRD',
        render: (card, {classes, scalar}) => (card.crd_id ? (
            <div className={classes.value}>
                <Imported imported={scalar('crd_id')} classes={classes}>{card.crd_id}</Imported>
            </div>
        ) : <Empty classes={classes}/>),
    },
    {
        label: 'LinkedIn',
        render: (card, {classes, scalar}) => (card.linkedin_url ? (
            <Imported imported={scalar('linkedin_url')} classes={classes}>
                <a
                    href={card.linkedin_url.startsWith('http') ? card.linkedin_url : `https://${card.linkedin_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.link}
                    style={scalar('linkedin_url') ? {color: IMPORTED} : undefined}
                >
                    {linkedinLabel(card.linkedin_url)}
                    <CallMade style={{fontSize: 14}}/>
                </a>
            </Imported>
        ) : <Empty classes={classes}/>),
    },
    {
        label: 'Gender',
        render: (card, {classes, scalar}) => (card.gender ? (
            <div className={classes.value}>
                <Imported imported={scalar('gender')} classes={classes}>{_.capitalize(card.gender)}</Imported>
            </div>
        ) : <Empty classes={classes}/>),
    },
    {
        label: 'Designations',
        render: (card, {classes, row}) => ((card.achievements || []).length === 0 ? <Empty classes={classes}/> : (
            <div className={classes.chips}>
                {card.achievements.map((achievement) => (
                    <Chip
                        key={achievement.id}
                        size="small"
                        label={achievement.name}
                        className={`${classes.designation} ${row('achievements', achievement) ? classes.importedDesignation : ''}`}
                    />
                ))}
            </div>
        )),
    },
    {
        label: 'Education',
        render: (card, {classes, row}) => (
            <ItemList items={card.universities || []} classes={classes} render={(university, index) => (
                <div key={`${university.university_id}-${index}`} className={classes.item}>
                    <Imported imported={row('universities', university)} classes={classes}>
                        <div>
                            <div className={classes.value}>{university.university_name}</div>
                            {years(university) && <div className={classes.secondary}>{years(university)}</div>}
                        </div>
                    </Imported>
                </div>
            )}/>
        ),
    },
    {
        label: 'Previous employment',
        render: (card, {classes, row}) => (
            <ItemList items={card.companies || []} classes={classes} render={(company, index) => (
                <div key={`${company.display_id}-${index}`} className={classes.item}>
                    <Imported imported={row('companies', company)} classes={classes}>
                        <div>
                            <div className={classes.value}>{company.company_name || company.display_id}</div>
                            {years(company) && <div className={classes.secondary}>{years(company)}</div>}
                        </div>
                    </Imported>
                </div>
            )}/>
        ),
    },
];

const IdentityCell = ({card, isForm, changes, classes}) => (
    <div className={classes.identity}>
        <Avatar
            src={card.photo_url || undefined}
            alt={card.name}
            className={`${classes.avatar} ${!isForm && changes?.photo && card.photo_url ? classes.importedAvatar : ''}`}
        >
            {(card.name || '?').charAt(0)}
        </Avatar>
        <div>
            <h3 className={classes.name}>
                <Imported imported={!isForm && isImportedScalar(changes, 'name', card.name)} classes={classes}>
                    {card.name || '-'}
                </Imported>
            </h3>
            <Chip
                size="small"
                label={isForm ? 'This form' : [DATASET_LABELS[card.dataset] || card.dataset, card.state].filter(Boolean).join(' · ')}
                color={isForm ? 'primary' : 'default'}
                className={classes.chip}
            />
        </div>
    </div>
);

// The form next to every record the matched person holds, with what the import would bring
// into the form in green.
const MatchReviewModal = ({open, onClose, onAccept, records = [], formCard, importedCard}) => {
    const classes = useStyles();
    if (!open || !formCard) return null;

    const changes = importedChanges(formCard, importedCard);
    const datasets = _.uniq(records.map(({dataset}) => dataset));
    const columns = [{card: formCard, isForm: true, key: 'form'}, ...records.map((record) => ({card: record, isForm: false, key: record.key}))];

    const cellClass = (column, extra = '') => (
        `${classes.cell} ${column.isForm ? classes.formCell : ''} ${extra}`
    );
    const helpersFor = (column) => ({
        classes,
        scalar: (field) => !column.isForm && isImportedScalar(changes, field, column.card[field]),
        row: (field, item) => !column.isForm && isImportedRow(changes, field, item),
    });

    return (
        <Modal open={open} onClose={onClose}>
            <Paper className={classes.modal}>
                <div className={classes.header}>
                    <h2 className={classes.title}>Person Preview</h2>
                    <div className={classes.headerMeta}>
                        {changes && <span className={classes.legend}><span className={classes.dot}/>Will be imported into the form</span>}
                        <span>Found in {datasets.length} {datasets.length === 1 ? 'dataset' : 'datasets'}</span>
                    </div>
                </div>
                <div className={classes.scroller}>
                    <div
                        className={classes.grid}
                        style={{gridTemplateColumns: `${LABEL_COLUMN}px repeat(${columns.length}, ${RECORD_COLUMN}px)`}}
                    >
                        <div className={`${classes.cell} ${classes.headCell}`}/>
                        {columns.map((column) => (
                            <div
                                key={`head-${column.key}`}
                                className={cellClass(column, `${classes.headCell} ${column.isForm ? classes.formHeadCell : ''}`)}
                            >
                                <IdentityCell card={column.card} isForm={column.isForm} changes={changes} classes={classes}/>
                            </div>
                        ))}

                        {ROWS.map((row) => (
                            <React.Fragment key={row.label}>
                                <div className={`${classes.cell} ${classes.labelCell}`}>{row.label}</div>
                                {columns.map((column) => (
                                    <div key={`${row.label}-${column.key}`} className={cellClass(column)}>
                                        {row.render(column.card, helpersFor(column))}
                                    </div>
                                ))}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
                <div className={classes.footer}>
                    <Button onClick={onClose}>Close</Button>
                    {onAccept && (
                        <Button onClick={onAccept} variant="contained" color="primary" disableElevation>
                            Yes, link
                        </Button>
                    )}
                </div>
            </Paper>
        </Modal>
    );
};

export default MatchReviewModal;
